/**
 * Shared equipment definitions for Digital Twin visualization.
 * Used by EquipmentLabel (floating 3D labels) and EquipmentDetailPanel (detail overlay).
 */

const TEAL = '#5fd4c4';
const GOLD = '#d4af37';
const RED = '#ff4444';

/**
 * Metric definitions per equipment type.
 * Each metric specifies: key (from sim state), display label, unit, optional formatter, and color.
 */
export const METRIC_DEFS = {
    solar: [
        { key: 'power_kw', label: 'Power', unit: 'kW', color: TEAL },
        { key: 'capacity_kw', label: 'Capacity', unit: 'kW', color: '#888' },
        { key: 'irradiance', label: 'Irradiance', unit: 'W/m\u00B2', color: GOLD },
    ],
    battery: [
        { key: 'soc', label: 'SOC', unit: '%', format: (v) => `${(v * 100).toFixed(1)}`, color: TEAL },
        { key: 'power_kw', label: 'Power', unit: 'kW', color: GOLD },
        { key: 'capacity_kwh', label: 'Capacity', unit: 'kWh', color: '#888' },
        { key: 'cycles', label: 'Cycles', unit: '', color: '#888' },
    ],
    ev_fleet: [
        { key: 'vehicles_charging', label: 'Charging', unit: 'vehicles', color: TEAL },
        { key: 'total_vehicles', label: 'Total', unit: 'vehicles', color: '#888' },
        { key: 'power_kw', label: 'Power', unit: 'kW', color: GOLD },
        { key: 'avg_soc', label: 'Avg SOC', unit: '%', format: (v) => `${(v * 100).toFixed(0)}`, color: TEAL },
    ],
    cold_storage: [
        { key: 'temperature_f', label: 'Temp', unit: '\u00B0F', color: '#aaddff' },
        { key: 'compressor_on', label: 'Compressor', unit: '', format: (v) => v ? 'ON' : 'OFF', color: (v) => v ? TEAL : RED },
        { key: 'power_kw', label: 'Power', unit: 'kW', color: GOLD },
    ],
    grid: [
        { key: 'power_kw', label: 'Power', unit: 'kW', color: (v) => v > 0 ? RED : TEAL },
        { key: 'direction', label: 'Direction', unit: '', format: (v) => v || 'idle', color: '#888' },
        { key: 'peak_kw', label: 'Peak', unit: 'kW', color: GOLD },
    ],
    building_load: [
        { key: 'power_kw', label: 'Load', unit: 'kW', color: GOLD },
        { key: 'peak_kw', label: 'Peak', unit: 'kW', color: RED },
    ],
};

export const EQUIPMENT_LABELS = {
    solar: 'SOLAR ARRAY',
    battery: 'BATTERY ESS',
    ev_fleet: 'EV FLEET',
    cold_storage: 'COLD STORAGE',
    grid: 'GRID CONNECTION',
    building_load: 'BUILDING LOAD',
};

/**
 * Primary metrics to show as floating labels in the 3D scene.
 * Each entry: [metricKey, formatFn]. Maximum 2 metrics per label for readability.
 */
export const LABEL_METRICS = {
    solar: [
        { key: 'power_kw', label: 'Power', unit: 'kW' },
    ],
    battery: [
        { key: 'soc', label: 'SOC', unit: '%', format: (v) => (v * 100).toFixed(0) },
        { key: 'power_kw', label: 'Power', unit: 'kW' },
    ],
    ev_fleet: [
        { key: 'vehicles_charging', label: 'Charging', unit: '' },
        { key: 'power_kw', label: 'Power', unit: 'kW' },
    ],
    cold_storage: [
        { key: 'temperature_f', label: 'Temp', unit: '\u00B0F' },
        { key: 'power_kw', label: 'Power', unit: 'kW' },
    ],
    grid: [
        { key: 'power_kw', label: 'Power', unit: 'kW' },
    ],
    building_load: [
        { key: 'power_kw', label: 'Load', unit: 'kW' },
    ],
};

/**
 * Y-offset above the equipment group origin for floating labels.
 * Based on each equipment mesh's height.
 */
export const LABEL_Y_OFFSETS = {
    solar: 6,
    battery: 5,
    ev_fleet: 4,
    cold_storage: 7,
    grid: 10,
    building_load: 12,
};

/**
 * Base layout positions for each equipment type.
 * Building at origin. Others arranged around it.
 * Y values are ground-level (meshes raise themselves).
 */
export const LAYOUT_POSITIONS = {
    building_load: [0, 0, 0],
    solar:         [-14, 0, -16],
    battery:       [18, 0, -2],
    grid:          [-30, 0, 0],
    ev_fleet:      [16, 0, 16],
    cold_storage:  [-16, 0, 16],
};

/**
 * Port positions (local to equipment origin) where conduits attach.
 * In LOCAL space — add to LAYOUT_POSITIONS for world coords.
 */
export const EQUIPMENT_PORTS = {
    solar:         { output: [3, 1.5, 2] },
    battery:       { input: [-3, 1.5, 0], output: [3, 1.5, 0] },
    building_load: { input_left: [-7, 3, 0], input_right: [7, 3, 0], output_front: [0, 2, 5] },
    ev_fleet:      { input: [-3, 1, 0] },
    cold_storage:  { input: [5, 2, 0] },
    grid:          { output: [6, 2, 0], input: [6, 2, 0] },
};

/**
 * Compute world-space port position.
 */
export function getWorldPort(equipmentType, portName) {
    const base = LAYOUT_POSITIONS[equipmentType];
    const port = EQUIPMENT_PORTS[equipmentType]?.[portName];
    if (!base || !port) return null;
    return [base[0] + port[0], base[1] + port[1], base[2] + port[2]];
}
